import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { VStack, HStack } from "@whoosh/design-system/src/components/primitive";

import { SlotLockCountdownTimer } from "../../../LockSlots/components/SlotLockCountdownTimer/SlotLockCountdownTimer";
import { navigateToPreviousStepAtom } from "../../atoms/actions/navigateToPreviousStep/navigateToPreviousStepAtom";
import { canAdvanceToNextStepSelectorAtom } from "../../atoms/selectors/canAdvanceToNextStepSelectorAtom/canAdvanceToNextStepSelectorAtom";
import { startSlotIdSelectorAtom } from "../../atoms/selectors/startSlotIdSelectorAtom/startSlotIdSelectorAtom";
import {
  CurrentBookingWizardStepAtom,
  ListOfBookingWizardStepsAtom,
  BookingWizardDataAtom,
  isAddingNewGuestAtom,
  CurrentRequestGroupIndexAtom,
} from "../../atoms/stores";
import { WizardFooter_Node$key } from "./__generated__/WizardFooter_Node.graphql";
import { usePressContinue } from "./hooks/usePressContinue/usePressContinue";

export const isFirstStepAtom = atom<boolean>((get) => {
  const currentStep = get(CurrentBookingWizardStepAtom);
  const currentGroupIndex = get(CurrentRequestGroupIndexAtom);

  const requiredSteps = get(ListOfBookingWizardStepsAtom);

  const stepIndex = requiredSteps?.findIndex((step) => step === currentStep);

  return stepIndex === 0 && currentGroupIndex === 0;
});

type Props = {
  currentServerTime: string | null | undefined;
  timeSlotRef: WizardFooter_Node$key | null;
  onClose: () => void;
  setIsCompletingBooking: (isCompletingBooking: boolean) => void;
};

export const WizardFooter = ({
  currentServerTime,
  timeSlotRef,
  onClose,
  setIsCompletingBooking,
}: Props) => {
  const timeSlot = useFragment(WizardFooterFragmentString, timeSlotRef);
  const { handleWizardContinueOrComplete, isCompletingBooking } =
    usePressContinue({ onClose });

  useEffect(() => {
    if (isCompletingBooking) {
      setIsCompletingBooking(true);
    }
    return () => {
      setIsCompletingBooking(false);
    };
  }, [isCompletingBooking, setIsCompletingBooking]);

  const isFirstStep = useAtomValue(isFirstStepAtom);

  const bookingWizardData = useAtomValue(BookingWizardDataAtom);

  const goToPreviousStep = useSetAtom(navigateToPreviousStepAtom);

  const currentStep = useAtomValue(CurrentBookingWizardStepAtom);

  const isLessonRequest =
    bookingWizardData?.type === "LessonBooking" && bookingWizardData.isRequest;

  const canAdvanceToNextStep = useAtomValue(canAdvanceToNextStepSelectorAtom);
  const isAddingNewGuest = useAtomValue(isAddingNewGuestAtom);

  const startSlotId = useAtomValue(startSlotIdSelectorAtom);
  const hasSelectedStartSlot =
    typeof startSlotId === "string" && startSlotId.length > 0;
  const isRequestBookingMissingRequestedTime =
    bookingWizardData?.type === "RequestBooking" &&
    !bookingWizardData.requestedTime;
  const isLessonRequestMissingTimeWindow =
    isLessonRequest &&
    (!bookingWizardData?.requestedTime ||
      !bookingWizardData?.minTime ||
      !bookingWizardData?.maxTime);
  const isDisabled =
    !canAdvanceToNextStep ||
    !hasSelectedStartSlot ||
    isRequestBookingMissingRequestedTime ||
    isLessonRequestMissingTimeWindow ||
    isAddingNewGuest;

  const continueText =
    currentStep === "PreviousBookings"
      ? t`Continue with a New Group`
      : t`Continue`;

  return (
    <VStack padding={5} borderTopWidth={1.5} borderTopColor="outline-default">
      <HStack justifyContent="center">
        {timeSlot?.__typename === "TimeSlot" ? (
          <SlotLockCountdownTimer
            agendaId={timeSlot.agenda?.id}
            currentServerTime={currentServerTime}
            lockedUntil={timeSlot?.lockedUntil ?? null}
            timeSlotId={timeSlot.id}
            onExpireCallback={onClose}
          />
        ) : null}
      </HStack>
      <HStack paddingTop={5} gap={4}>
        {!isFirstStep ? (
          <Button
            width={120}
            variant="secondary"
            testID="booking-wizard-back-button"
            accessibilityLabel={t`Back button`}
            onPress={goToPreviousStep}
          >{t`Back`}</Button>
        ) : null}
        <Button
          style={{ flex: 1 }}
          onPress={handleWizardContinueOrComplete}
          testID="booking-wizard-continue-button"
          accessibilityLabel={t`Booking Wizard Continue Button`}
          isDisabled={isDisabled}
        >
          {continueText}
        </Button>
      </HStack>
    </VStack>
  );
};

const WizardFooterFragmentString = graphql`
  fragment WizardFooter_Node on Node {
    __typename
    ... on TimeSlot {
      id
      lockedUntil
      agenda {
        id
      }
    }
  }
`;
